import React, { useState } from 'react';
import { Search, ArrowRight, Newspaper, GitCompare, TrendingUp, FileUser, Info } from 'lucide-react';
import { TAPE_NAME } from '../../config/tapeConfig.ts';
import type { TapeActionId, TapeDepth, TapeModel } from '../../services/tape/tapeTypes.ts';
import { useTapeModel } from '../../services/tape/useTapeModel.ts';
import { useTapeFeed } from '../../services/tape/tapeFeed.ts';

export interface TapeLaunch {
  action: TapeActionId;
  person?: string;
  personB?: string;
  topic?: string;
  /** Read In input. */
  ticker?: string;
  /** Read In starting depth. */
  depth?: TapeDepth;
}

interface SecondaryAction {
  id: Exclude<TapeActionId, 'readin' | 'timeline'>;
  title: string;
  desc: string;
  icon: React.FC<{ className?: string; style?: React.CSSProperties }>;
  example: { label: string; launch: TapeLaunch };
  /** When true, render a "PREVIEW" pill next to the title. The action is
   *  reachable but the user knows it's curated demo content only. */
  preview?: boolean;
}

const SECONDARY: SecondaryAction[] = [
  {
    id: 'brief',
    title: "Get this week's read",
    desc: 'What the desks actually said this week on one topic, synthesized, with the quotes behind it.',
    icon: Newspaper,
    example: { label: 'oil & the Strait of Hormuz', launch: { action: 'brief', topic: 'oil & the Strait of Hormuz' } },
  },
  {
    id: 'split',
    title: 'Put two camps head to head',
    desc: 'Where the bulls and bears actually agree and diverge on one debate, side by side, with receipts.',
    icon: GitCompare,
    example: { label: 'the AI bubble: bulls vs bears', launch: { action: 'split', person: 'The bears', personB: 'The bulls', topic: 'the AI bubble' } },
  },
  {
    id: 'dossier',
    title: "Compile a person's positions",
    desc: 'A research dossier on a finance voice — every stated position grouped by topic, every quote sourced and playable.',
    icon: FileUser,
    example: { label: 'Mohamed El-Erian', launch: { action: 'dossier', person: 'Mohamed El-Erian' } },
    preview: true,
  },
  {
    id: 'narrative',
    title: 'Watch the narrative drift',
    desc: 'How the prevailing view on one topic moved over years — with the bucketed quotes behind it and the inflection points called out.',
    icon: TrendingUp,
    example: { label: 'the sovereign debt endgame (Gromen)', launch: { action: 'narrative', topic: 'the sovereign debt endgame', person: 'Luke Gromen' } },
    preview: true,
  },
];

/** Fallback ticker pills shown while the personalized feed loads or if the
 *  /api/tape/feed call fails. Lets the launcher always have something to
 *  click. */
const FALLBACK_TICKERS = ['APP', 'NVDA', 'CRWV'];

/** Small mono PREVIEW pill — used in the launcher next to surfaces we've
 *  marked as canon-only for v1 (Dossier, Narrative). */
const PreviewPill: React.FC = () => (
  <span
    className="tape-mono inline-flex items-center rounded-full border px-1.5 py-px text-[9px] uppercase tracking-wider"
    style={{ borderColor: 'var(--tape-accent)', color: 'var(--tape-accent)' }}
  >
    Preview
  </span>
);

/** Synthesis-tier toggle (global, persisted via useTapeModel). Quiet by
 *  design — Deep is the recommended default and most users won't touch it.
 *  The (i) button reveals a short explainer popover. */
const SynthesisModeToggle: React.FC<{ model: TapeModel; onChange: (m: TapeModel) => void }> = ({ model, onChange }) => {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="relative flex items-center justify-center gap-2">
      <span className="tape-label">Synthesis</span>
      <div className="inline-flex items-center rounded-full border p-0.5 text-[11px]" style={{ borderColor: 'var(--tape-hairline-strong)' }}>
        {([['quality', 'Deep'], ['fast', 'Fast']] as const).map(([id, label]) => {
          const active = model === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className="tape-mono rounded-full px-3 py-1 uppercase tracking-wide transition-colors"
              style={{
                background: active ? 'var(--tape-accent)' : 'transparent',
                color: active ? 'var(--tape-bg)' : 'var(--tape-fg-dim)',
              }}
              title={id === 'quality' ? 'Recommended. Most capable models.' : 'Lighter models, faster turnaround.'}
            >
              {label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setShowInfo(v => !v)}
        aria-label="Explain synthesis modes"
        className="rounded-full p-1 transition-opacity hover:opacity-100"
        style={{ color: 'var(--tape-fg-faint)', opacity: showInfo ? 1 : 0.7 }}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {showInfo && (
        <div
          className="absolute left-1/2 top-full z-30 mt-2 w-80 -translate-x-1/2 rounded border p-3.5 text-[12px] leading-relaxed shadow-lg"
          style={{ background: 'var(--tape-bg)', borderColor: 'var(--tape-hairline-strong)', color: 'var(--tape-fg-dim)' }}
        >
          <p>
            <span className="tape-mono uppercase tracking-wide" style={{ color: 'var(--tape-accent)' }}>Deep</span>
            <span className="ml-1.5 text-[10px] uppercase tracking-wider" style={{ color: 'var(--tape-fg-faint)' }}>recommended</span>
          </p>
          <p className="mt-1">Most capable models. Exhaustive synthesis. ~60–90 seconds per novel query. Cached results return instantly.</p>
          <p className="mt-3">
            <span className="tape-mono uppercase tracking-wide" style={{ color: 'var(--tape-fg)' }}>Fast</span>
          </p>
          <p className="mt-1">Lighter models. Direct answers. ~30–45 seconds. Choose this when speed matters more than depth.</p>
          <button
            type="button"
            onClick={() => setShowInfo(false)}
            className="mt-3 text-[10px] uppercase tracking-wide opacity-60 hover:opacity-100"
            style={{ color: 'var(--tape-fg-faint)' }}
          >
            close
          </button>
        </div>
      )}
    </div>
  );
};

const TapeCommandSurface: React.FC<{ onLaunch: (launch: TapeLaunch) => void }> = ({ onLaunch }) => {
  const [query, setQuery] = useState('');
  const [synthModel, setSynthModel] = useTapeModel();
  const { state: feedState } = useTapeFeed();

  const submit = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    onLaunch({ action: 'readin', ticker: v.toUpperCase() });
  };

  // Feed-derived ticker chips. Always render *something*: when the feed is
  // loading or errored, fall back to the hardcoded sampler so the launcher
  // doesn't look empty.
  const feed = feedState.kind === 'ready' ? feedState.feed : null;
  const tickers = feed && feed.tickers.length > 0
    ? feed.tickers
    : FALLBACK_TICKERS.map(t => ({ ticker: t, reason: 'generic' as const, ready: false, name: undefined }));
  const briefs = feed?.briefs ?? [];
  const personaApplied = feed?.personaApplied ?? false;

  return (
    <div className="mx-auto w-full max-w-xl px-5 pb-20 pt-16 sm:pt-24">
      {/* wordmark + value prop */}
      <div className="tape-fade text-center">
        <h1 className="tape-serif text-5xl tracking-tight sm:text-6xl" style={{ color: 'var(--tape-fg)' }}>
          {TAPE_NAME}
        </h1>
        <p className="mt-3 text-lg font-medium sm:text-xl" style={{ color: 'var(--tape-fg)' }}>
          Skip the prattle. Extract the alpha.
        </p>
        <p className="mx-auto mt-2.5 max-w-md text-[14px] leading-relaxed" style={{ color: 'var(--tape-fg-dim)' }}>
          Citations from podcast voices you already trust. Condensed for alpha - Bloomberg, Real Vision, Macro Voices and the rest of the macro feed, searchable. 
        </p>
      </div>

      {/* hero: ticker → Read-in */}
      <form
        onSubmit={e => { e.preventDefault(); submit(query); }}
        className="tape-fade mt-10"
        style={{ animationDelay: '70ms' }}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2" style={{ color: 'var(--tape-fg-faint)' }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value.toUpperCase())}
            placeholder="Read in on any ticker…"
            autoFocus
            spellCheck={false}
            className="tape-search w-full py-3.5 pl-12 pr-28 uppercase"
          />
          <button
            type="submit"
            disabled={!query.trim()}
            className="tape-btn tape-btn--go absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5 px-3.5 py-2"
          >
            Read in
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-1">
          <span className="text-xs" style={{ color: 'var(--tape-fg-faint)' }}>
            {personaApplied ? 'For you' : 'Try'}
          </span>
          {tickers.map(t => {
            const isPersona = t.reason === 'persona';
            return (
              <button
                key={t.ticker}
                type="button"
                onClick={() => submit(t.ticker)}
                className="tape-pill px-2.5 py-1"
                style={isPersona ? {
                  // Subtle accent tint for persona-driven picks. No icon —
                  // the styling does the signaling.
                  borderColor: 'var(--tape-accent-line)',
                  color: 'var(--tape-fg)',
                } : undefined}
                // "cached — instant" stays in the tooltip for power users
                // but doesn't earn a visible badge on every pill.
                title={
                  [t.name, isPersona ? 'from your brief' : null, t.ready ? 'cached — instant' : null]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
              >
                {t.ticker}
              </button>
            );
          })}
        </div>
      </form>

      {/* Personalized brief recommendations. Hidden when backend returns
          nothing — no point in an empty section header. */}
      {briefs.length > 0 && (
        <div className="tape-fade mt-8" style={{ animationDelay: '100ms' }}>
          <div className="tape-label mb-2.5 pl-1">
            {personaApplied ? 'Briefs for you' : 'On the desks'}
          </div>
          <div className="tape-panel tape-divide overflow-hidden">
            {briefs.map((b, i) => (
              <button
                key={`${b.query}-${i}`}
                type="button"
                onClick={() => onLaunch({ action: 'brief', topic: b.query })}
                className="tape-action relative flex w-full items-center justify-between px-4 py-3 text-left"
                style={b.personalized ? {
                  // Tiny accent rail on the left edge signals "this one's
                  // yours" without resorting to an icon.
                  boxShadow: 'inset 3px 0 0 0 var(--tape-accent-line)',
                } : undefined}
                title={b.personalized ? 'From your read' : undefined}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Newspaper className="h-[16px] w-[16px] flex-shrink-0" style={{ color: 'var(--tape-accent)' }} />
                  <span className="tape-serif text-[15px] leading-snug" style={{ color: 'var(--tape-fg)' }}>
                    {b.title}
                  </span>
                </div>
                <ArrowRight className="tape-action-arrow h-4 w-4 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Global synthesis mode — set once, applies to every action. Placed
          above the "Or go deeper" list so users see it before drilling in.
          `relative z-40` ensures the (i) popover layers over the panel below. */}
      <div className="tape-fade relative z-40 mt-8" style={{ animationDelay: '120ms' }}>
        <SynthesisModeToggle model={synthModel} onChange={setSynthModel} />
      </div>

      {/* the other actions */}
      <div className="tape-fade mt-8" style={{ animationDelay: '140ms' }}>
        <div className="tape-label mb-2.5 pl-1">Or go deeper</div>
        <div className="tape-panel tape-divide overflow-hidden">
          {SECONDARY.map(a => {
            const Icon = a.icon;
            return (
              <div key={a.id} className="tape-action px-4 py-4">
                <div className="flex min-w-0 items-start gap-3.5">
                  <Icon className="mt-0.5 h-[18px] w-[18px] flex-shrink-0" style={{ color: 'var(--tape-accent)' }} />
                  <div className="min-w-0">
                    <button type="button" onClick={() => onLaunch({ action: a.id })} className="block text-left">
                      <span className="tape-serif inline-flex items-baseline gap-2 text-[17px] leading-snug" style={{ color: 'var(--tape-fg)' }}>
                        <span>{a.title}</span>
                        {a.preview && <PreviewPill />}
                      </span>
                      <span className="mt-1 block text-[13px] leading-relaxed" style={{ color: 'var(--tape-fg-dim)' }}>{a.desc}</span>
                    </button>
                    <button type="button" onClick={() => onLaunch(a.example.launch)} className="tape-pill mt-2.5 inline-block px-2.5 py-1">
                      {a.example.label}
                    </button>
                  </div>
                </div>
                <button type="button" onClick={() => onLaunch({ action: a.id })} aria-label={a.title} className="flex-shrink-0">
                  <ArrowRight className="tape-action-arrow h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TapeCommandSurface;
